import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  getDocs
} from "firebase/firestore";
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

export default function ParentDashboard() {
  const { currentUser, userProfile, logout } = useAuth();
  const [children, setChildren] = useState([]);
  const [childBooks, setChildBooks] = useState([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [childName, setChildName] = useState("");
  const [childEmail, setChildEmail] = useState("");
  const [childPassword, setChildPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, "users"),
      where("parentId", "==", currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const childList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setChildren(childList);
    });
    return unsubscribe;
  }, [currentUser]);

  // Fetches every book belonging to any of this parent's children in one
  // listener. We depend on a joined string of child IDs (not the array ref)
  // so we only re-subscribe when the actual set of children changes — not
  // on every unrelated field update (same onSnapshot lesson from AuthContext).
  const childIdsKey = children.map((c) => c.id).sort().join(",");
  useEffect(() => {
    if (children.length === 0) {
      setChildBooks([]);
      return;
    }
    const childIds = children.map((c) => c.id);
    const q = query(collection(db, "books"), where("userId", "in", childIds));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setChildBooks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childIdsKey]);

  // Quizzes across all of one child's books that are waiting on parent review
  function getReviewAlertCount(childId) {
    return childBooks
      .filter((b) => b.userId === childId)
      .reduce((sum, book) => {
        const ready = (book.quizzes || []).filter(
          (q) => q.completedByChild && !q.reviewedByParent && !q.needsRevision
        ).length;
        return sum + ready;
      }, 0);
  }

  const totalReviewAlerts = children.reduce(
    (sum, child) => sum + getReviewAlertCount(child.id),
    0
  );

async function createChildAccount() {
    if (!childName.trim()) return setError("Please enter the child's name.");
    if (!childEmail.trim()) return setError("Please enter the child's email.");
    if (childPassword.length < 6) return setError("Password must be at least 6 characters.");

    setLoading(true);
    setError("");

    try {
      // Create a secondary app instance so parent stays logged in
      const secondaryApp = initializeApp(
        {
          apiKey: "AIzaSyAvf_ZCEMkBlpR2PUS7J910ccf8qjCDyn0",
          authDomain: "reading-log-d5d0d.firebaseapp.com",
          projectId: "reading-log-d5d0d",
          storageBucket: "reading-log-d5d0d.firebasestorage.app",
          messagingSenderId: "923951064641",
          appId: "1:923951064641:web:3b9ca2c9c0995b7f2088c0"
        },
        "secondary"
      );

      const secondaryAuth = getAuth(secondaryApp);
      const result = await createUserWithEmailAndPassword(
        secondaryAuth,
        childEmail,
        childPassword
      );

      // Save child profile linked to parent
      await setDoc(doc(db, "users", result.user.uid), {
        name: childName,
        email: childEmail,
        role: "child",
        parentId: currentUser.uid,
        parentName: userProfile?.name || "",
        createdAt: new Date()
      });

      // Sign out of secondary app
      await secondaryAuth.signOut();

      setChildName("");
      setChildEmail("");
      setChildPassword("");
      setShowAddChild(false);
      alert(`${childName}'s account has been created successfully!`);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("That email is already in use.");
      } else {
        setError("Failed to create account. Try again.");
      }
    }
    setLoading(false);
  }

  async function getChildBooks(childId) {
    const q = query(collection(db, "books"), where("userId", "==", childId));
    const snapshot = await getDocs(q);
    return snapshot.docs.length;
  }

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div style={styles.container}>
      <Header />

      <div style={styles.content}>
        <div style={styles.topRow}>
          <h2 style={styles.sectionTitle}>My Children</h2>
          <button style={styles.addButton} onClick={() => setShowAddChild(!showAddChild)}>
            {showAddChild ? "Cancel" : "+ Add Child"}
          </button>
        </div>

        {/* Quiz review alert banner */}
        {totalReviewAlerts > 0 && (
          <div style={styles.alertBanner}>
            <p style={styles.alertText}>
              📬 {totalReviewAlerts} quiz{totalReviewAlerts > 1 ? "zes are" : " is"} waiting for your review — look for the badge below.
            </p>
          </div>
        )}

        {/* Add Child Form */}
        {showAddChild && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Create Child Account</h3>
            {error && <p style={styles.error}>{error}</p>}
            <label style={styles.label}>Child's Full Name</label>
            <input
              style={styles.input}
              type="text"
              placeholder="Enter child's name"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
            />
            <label style={styles.label}>Child's Email</label>
            <input
              style={styles.input}
              type="email"
              placeholder="Enter child's email"
              value={childEmail}
              onChange={(e) => setChildEmail(e.target.value)}
            />
            <label style={styles.label}>Temporary Password</label>
            <input
              style={styles.input}
              type="password"
              placeholder="Create a temporary password"
              value={childPassword}
              onChange={(e) => setChildPassword(e.target.value)}
            />
            <p style={styles.hint}>
              Share these credentials with your child so they can log in and change their password.
            </p>
            <button style={styles.createBtn} onClick={createChildAccount} disabled={loading}>
              {loading ? "Creating..." : "Create Account"}
            </button>
          </div>
        )}

        {/* Children List */}
        {children.length === 0 && !showAddChild ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No children added yet.</p>
            <p style={styles.emptySubtext}>Tap + Add Child to create their account.</p>
          </div>
        ) : (
          <div style={styles.childGrid}>
            {children.map((child) => {
              const reviewCount = getReviewAlertCount(child.id);
              return (
                <div
                  key={child.id}
                  style={styles.childCard}
                  onClick={() => navigate(`/parent/child/${child.id}`)}
                >
                  <div style={styles.childAvatar}>
                    {child.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={styles.childInfo}>
                    <h3 style={styles.childName}>{child.name}</h3>
                    <p style={styles.childEmail}>{child.email}</p>
                  </div>
                  {reviewCount > 0 && (
                    <span style={styles.reviewBadge}>📬 {reviewCount}</span>
                  )}
                  <div style={styles.childArrow}>→</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },
  header: { backgroundColor: "white", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" },
  title: { fontSize: 24, color: "#2d3748", margin: 0 },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  userName: { color: "#4a5568", fontSize: 14 },
  myBooksBtn: { padding: "8px 16px", backgroundColor: "#4299e1", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14 },
  logoutBtn: { padding: "8px 16px", backgroundColor: "#e2e8f0", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14 },
  content: { padding: 24, maxWidth: 700, margin: "0 auto" },
  topRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  sectionTitle: { fontSize: 20, color: "#2d3748", margin: 0 },
  addButton: { padding: "10px 20px", backgroundColor: "#48bb78", color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer" },
  alertBanner: { backgroundColor: "#ebf8ff", border: "2px solid #4299e1", borderRadius: 12, padding: "14px 18px", marginBottom: 16 },
  alertText: { fontSize: 14, color: "#2b6cb0", fontWeight: "bold", margin: 0 },
  card: { backgroundColor: "white", borderRadius: 12, padding: 24, marginBottom: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" },
  cardTitle: { fontSize: 18, color: "#2d3748", marginTop: 0, marginBottom: 16 },
  label: { display: "block", fontWeight: "bold", color: "#4a5568", marginBottom: 6, fontSize: 14 },
  input: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 16, boxSizing: "border-box", marginBottom: 16 },
  hint: { color: "#a0aec0", fontSize: 13, margin: "0 0 16px 0" },
  createBtn: { width: "100%", padding: 14, backgroundColor: "#48bb78", color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer" },
  error: { color: "#f56565", marginBottom: 12, fontSize: 14 },
  emptyState: { textAlign: "center", padding: 60 },
  emptyText: { fontSize: 20, color: "#4a5568" },
  emptySubtext: { fontSize: 14, color: "#a0aec0" },
  childGrid: { display: "flex", flexDirection: "column", gap: 12 },
  childCard: { backgroundColor: "white", borderRadius: 12, padding: 20, boxShadow: "0 2px 4px rgba(0,0,0,0.1)", cursor: "pointer", display: "flex", alignItems: "center", gap: 16 },
  childAvatar: { width: 48, height: 48, borderRadius: 24, backgroundColor: "#4299e1", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: "bold" },
  childInfo: { flex: 1 },
  childName: { fontSize: 18, color: "#2d3748", margin: "0 0 4px 0" },
  childEmail: { fontSize: 14, color: "#718096", margin: 0 },
  reviewBadge: { backgroundColor: "#e53e3e", color: "white", fontSize: 12, fontWeight: "bold", borderRadius: 10, padding: "4px 10px", whiteSpace: "nowrap" },
  childArrow: { fontSize: 20, color: "#a0aec0" }
};